/**
 * This file manages authentication state and logic using Redux Toolkit's createSlice and createAsyncThunk.
 * It handles login, logout, registration, session checking, password reset, and password update flows.
 * State is persisted in Redux and interacts with Supabase for authentication.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../../shared/services/supabase/client';
import { AuthError } from '@supabase/supabase-js';
import { createLogger } from '../../../shared/config/logger';
import { api } from '../../../shared/services/api/client';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
const logger = createLogger('authSlice.js');

/**
 * Initial state for authentication
 * @property {string|null} accessToken - JWT access token
 * @property {string|null} refreshToken - JWT refresh token
 * @property {boolean} loading - Indicates if an async action is in progress
 * @property {string|null} error - Stores error messages
 * @property {string|null} sessionId - Supabase session ID
 * @property {string|null} userId - Authenticated user's ID
 * @property {boolean} loyalpassAdmin - Platform admin (from GET /auth/me)
 */
export const initialState = {
    accessToken: null, // JWT access token
    refreshToken: null, // JWT refresh token
    loading: true, // True until initial session check completes; prevents redirect on refresh
    error: null, // Stores error messages
    sessionId: null, // Supabase session ID
    userId: null, // Authenticated user's ID
    loyalpassAdmin: false,
};

/**
 * Thunk to check the current session and update the state accordingly
 * Calls Supabase to get the current session and updates Redux state.
 */
export const checkSession = createAsyncThunk(
    'auth/checkSession',
    async (_, { rejectWithValue }) => {
        try {
            // Get the current session from Supabase
            const { data, error } = await supabase.auth.getSession();
            if (error) {
                throw new Error(error.message);
            }

            if (!data?.session) {
                return null; // No session means the user is not logged in
            }

            let loyalpassAdmin = false;
            try {
                const me = await api.get('/auth/me', {
                    headers: { Authorization: `Bearer ${data.session.access_token}` },
                });
                loyalpassAdmin = !!me.data?.loyalpassAdmin;
            } catch {
                loyalpassAdmin = false;
            }

            // Return the session data for the Redux action payload
            return {
                accessToken: data.session.access_token,
                refreshToken: data.session.refresh_token,
                sessionId: data.session.id || null,
                userId: data.session.user.id || null,
                loyalpassAdmin,
            };
        } catch (error) {
            // Handle and log errors
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : 'Unknown error, failed to check session';
            logger.error('Session check failed', error);
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Thunk for logging in a user with email and password
export const login = createAsyncThunk(
    'auth/login',
    async ({ email, password }, { rejectWithValue }) => {
        logger.info('Login attempt initiated', { email });
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (error) {
                throw error;
            }
            if (!data.session) {
                throw new Error('Login successful, but no session returned');
            }

            logger.info('Login successful', { userId: data.user?.id });

            let loyalpassAdmin = false;
            try {
                const me = await api.get('/auth/me', {
                    headers: { Authorization: `Bearer ${data.session.access_token}` },
                });
                loyalpassAdmin = !!me.data?.loyalpassAdmin;
            } catch {
                loyalpassAdmin = false;
            }

            return {
                accessToken: data.session.access_token,
                refreshToken: data.session.refresh_token,
                sessionId: data.session.id || null,
                userId: data.session.user.id || null,
                loyalpassAdmin,
            };
        } catch (error) {
            // Handle and log errors (Supabase may return AuthError-like objects without instanceof match)
            const raw =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : typeof error?.message === 'string'
                        ? error.message
                        : '';
            const lower = raw.toLowerCase();
            const errorMessage =
                lower.includes('invalid login') || lower.includes('invalid credentials')
                    ? 'Invalid email or password.'
                    : raw || 'Unable to sign in. Please try again.';
            logger.error('Login failed', error);
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Thunk for logging out the current user
export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
    logger.info('Logout attempt initiated');
    try {
        const { error } = await supabase.auth.signOut();
        if (error) {
            throw error;
        }
        logger.info('Logout successful');
    } catch (error) {
        // Handle and log errors
        const errorMessage =
            error instanceof AuthError
                ? error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error, failed to logout';
        logger.error('Logout failed', error);
        return rejectWithValue({ error: errorMessage });
    }
});

// Thunk for registering a new user (merchant)
export const register = createAsyncThunk(
    'auth/register',
    async ({ email, password, business_name }, { rejectWithValue }) => {
        logger.info('Sign up attempt (backend) initiated', { email, business_name });
        try {
            const response = await api.post('/auth/register', {
                email,
                password,
                business_name,
            });
            // Backend will not return a session; just success info
            return response.data;
        } catch (error) {
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : extractErrorMessage(error) || 'Unknown error, failed to register';
            logger.error('Registration via backend failed', { error: errorMessage });
            return rejectWithValue({ error: errorMessage });
        }
    }
);

/**
 * Build the redirect URL Supabase should embed in the password reset email.
 * Prefers REACT_APP_PASSWORD_RESET_URL when set so deployments can pin a
 * specific frontend URL; otherwise falls back to the current origin so the
 * link works in whatever environment (prod, staging, local) the user is in.
 */
const getPasswordResetRedirectUrl = () => {
    const explicit = process.env.REACT_APP_PASSWORD_RESET_URL?.trim();
    if (explicit) {
        const base = explicit.replace(/\/+$/, '');
        return base.endsWith('/update-password') ? base : `${base}/update-password`;
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin.replace(/\/+$/, '')}/update-password`;
    }
    return '/update-password';
};

// Thunk for sending a password reset email
export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ email }, { rejectWithValue }) => {
        logger.info('Password reset attempt initiated', { email });
        try {
            const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: getPasswordResetRedirectUrl(),
            });
            if (error) {
                throw error;
            }
            return data;
        } catch (error) {
            // Handle and log errors
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : 'Unknown error, failed to reset password';
            logger.error('Password reset failed', error);
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Thunk for updating the user's password
export const updatePassword = createAsyncThunk(
    'auth/updatePassword',
    async ({ password }, { rejectWithValue }) => {
        logger.info('Password update attempt initiated');
        try {
            // For password recovery, we need to use updateUser with the current session
            const { error } = await supabase.auth.updateUser({
                password: password,
            });

            if (error) {
                throw error;
            }

            // If successful, return the updated user data
            logger.info('Password updated successfully');
        } catch (error) {
            // Handle and log errors
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : 'Unknown error, failed to update password';
            logger.error('Password update failed', error);
            return rejectWithValue({ error: errorMessage });
        }
    }
);

/**
 * The authentication slice, containing reducers and extraReducers for async thunks
 * Handles all async thunk states (pending, fulfilled, rejected) for each auth action
 */
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        // Synchronous reducer to clear error state
        clearErrors: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Session check reducers
        builder
            .addCase(checkSession.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(checkSession.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                if (action.payload) {
                    state.accessToken = action.payload.accessToken;
                    state.refreshToken = action.payload.refreshToken;
                    state.sessionId = action.payload.sessionId || null;
                    state.userId = action.payload.userId || null;
                    state.loyalpassAdmin = !!action.payload.loyalpassAdmin;
                    logger.info('checkSession fulfilled with session data');
                } else {
                    // No active session
                    state.accessToken = null;
                    state.refreshToken = null;
                    state.sessionId = null;
                    state.userId = null;
                    state.loyalpassAdmin = false;
                    logger.info('checkSession fulfilled with no session');
                }
            })
            .addCase(checkSession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to check session';
                state.accessToken = null;
                state.refreshToken = null;
                state.sessionId = null;
                state.userId = null;
                state.loyalpassAdmin = false;
                logger.error('checkSession rejected', { error: state.error });
            })
            // Login reducers — do not toggle `loading`: App.jsx uses it only for the initial
            // session check; setting it here unmounts public routes and clears Login UI state.
            .addCase(login.pending, (state) => {
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.error = null;
                if (action.payload) {
                    state.accessToken = action.payload.accessToken;
                    state.refreshToken = action.payload.refreshToken;
                    state.sessionId = action.payload.sessionId || null;
                    state.userId = action.payload.userId || null;
                    state.loyalpassAdmin = !!action.payload.loyalpassAdmin;
                    logger.info('login fulfilled with session data');
                } else {
                    // Unexpected case, but handle it gracefully
                    state.accessToken = null;
                    state.refreshToken = null;
                    state.sessionId = null;
                    state.userId = null;
                    state.loyalpassAdmin = false;
                    logger.warn('login fulfilled but no payload received');
                }
            })
            .addCase(login.rejected, (state, action) => {
                state.error = action.payload?.error || 'Failed to login';
                state.accessToken = null;
                state.refreshToken = null;
                state.sessionId = null;
                state.userId = null;
                state.loyalpassAdmin = false;
                logger.error('login rejected', { error: state.error });
            })
            // Logout reducers (don't set loading so we don't show full-screen loader; user goes straight to landing)
            .addCase(logout.pending, (state) => {
                state.error = null;
            })
            .addCase(logout.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.accessToken = null;
                state.refreshToken = null;
                state.sessionId = null;
                state.userId = null;
                state.loyalpassAdmin = false;
            })
            .addCase(logout.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to logout for an unknown reason';
                // clear the tokens for extra security
                state.accessToken = null;
                state.refreshToken = null;
                state.sessionId = null;
                state.userId = null;
                state.loyalpassAdmin = false;
                logger.error('logout rejected', { error: state.error });
            })
            // Register reducers - custom handler (clears tokens on rejection)
            .addCase(register.pending, (state) => {
                state.error = null;
            })
            .addCase(register.fulfilled, (state) => {
                state.error = null;
            })
            .addCase(register.rejected, (state, action) => {
                state.error = action.payload?.error || action.error?.message || 'Failed to register';
                state.accessToken = null;
                state.refreshToken = null;
                state.sessionId = null;
                state.userId = null;
                logger.error('register rejected', { error: state.error });
            })
            // Reset password reducers - can use standard pattern
            .addCase(resetPassword.pending, (state) => {
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.error = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.error = action.payload?.error || action.error?.message || 'Failed to reset password';
                logger.error('resetPassword rejected', { error: state.error });
            })
            // Update password reducers - custom handler (clears tokens on rejection)
            .addCase(updatePassword.pending, (state) => {
                state.error = null;
            })
            .addCase(updatePassword.fulfilled, (state) => {
                state.error = null;
                logger.info('updatePassword fulfilled');
            })
            .addCase(updatePassword.rejected, (state, action) => {
                state.error = action.payload?.error || action.error?.message || 'Failed to update password';
                state.accessToken = null;
                state.refreshToken = null;
                state.sessionId = null;
                state.userId = null;
                state.loyalpassAdmin = false;
                logger.error('updatePassword rejected', { error: state.error });
            });
    },
});

// Export synchronous actions
export const { clearErrors } = authSlice.actions;

// Action creator to clear all slices (for logout)
export const clearAllSlices = () => ({
    type: 'LOGOUT',
});

// Export reducer for store configuration
export default authSlice.reducer;

/**
 * API OBJECT - Convenient access to all auth-related actions (thunks)
 */
export const authSliceAPI = {
    checkSession,
    login,
    logout,
    register,
    resetPassword,
    updatePassword,
};
